import axios, { AxiosRequestConfig, CancelToken } from "axios";
import { BaseResponse } from "types";
import auth from "./admin/auth";

const baseURL = import.meta.env.VITE_API_URL;
const webSocketURL = import.meta.env.VITE_WS_URL;
export const ebarimtURL = "https://ebarimt.techpartners.asia/api/";

export interface IParams {
  [key: string]: string;
}

export interface IHeader {
  [key: string]: any;
}

export interface IBody {
  [key: string]: any;
}

export interface IOption {
  body?: IBody;
  hasAuth?: boolean;
  headers?: IHeader;
  params?: IParams;
  cancelToken?: CancelToken;
  onUploadProgress?: (progressEvent: any) => void;
}

namespace http {
  export const getAuth = (hasAuth = false) =>
    hasAuth && auth.getToken()
      ? {
          Authorization: `Bearer ${auth.getToken()}`,
        }
      : { Authorization: `` };

  export const getIsFilterOrg: () => any = () =>
    auth.getIsFilterOrg()
      ? {
          "Is-Filter-Org": `${auth.getIsFilterOrg()}`,
        }
      : {};
  export const getOrg: () => any = () =>
    auth.getOrg()
      ? {
          "Org-Id": `${auth.getOrg()}`,
        }
      : {};

  export const genHeader = (hasAuth = false, headers = {}) =>
    Object.assign(
      headers,
      getAuth(hasAuth),
      { Locale: "mn" },
      getOrg(),
      getIsFilterOrg(),
    );

  export const handleError = (err: any, reject: any) => {
    if (err && err.response && err.response.data) {
      const data = err.response.data;
      if (typeof data === "object" && !Array.isArray(data)) {
        // keep the HTTP status reachable without changing the body's shape
        Object.defineProperty(data, "status", {
          value: err.response.status,
          enumerable: false,
          configurable: true,
        });
      }
      return reject(data);
    }
    if (err && err.response && err.response.status) {
      return reject({
        message: "Unhandled error",
        status: err.response.status,
      });
    }

    return reject({ message: "Unhandled error" });
  };

  export const requestRaw = async <T>(options: AxiosRequestConfig) => {
    return new Promise<T>((resolve, reject) => {
      axios
        .request<T>({
          baseURL,
          ...options,
        })
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => handleError(err, reject));
    });
  };

  export const request = async <T>(options: AxiosRequestConfig) => {
    return new Promise<BaseResponse<T>>((resolve, reject) => {
      axios
        .request<BaseResponse<T>>({
          baseURL,
          ...options,
        })
        .then((resp) => {
          resolve(resp.data);
        })
        .catch((err) => handleError(err, reject));
    });
  };

  export const getRaw = async <T>(
    url: string,
    options?: IOption,
  ): Promise<T> => {
    return await requestRaw<T>({
      method: "GET",
      url,
      params: options?.params,
      cancelToken: options?.cancelToken,
    }).then((data) => data);
  };

  export const get = async <T>(url: string, options?: IOption): Promise<T> => {
    return await request<T>({
      method: "GET",
      url,
      headers: genHeader(options?.hasAuth, options?.headers),
      params: options?.params,
      cancelToken: options?.cancelToken,
    }).then((data) => data.body);
  };

  export const post = async <T>(url: string, options?: IOption): Promise<T> => {
    return await request<T>({
      method: "POST",
      url,
      headers: genHeader(options?.hasAuth, options?.headers),
      data: options?.body,
      onUploadProgress: options?.onUploadProgress,
      cancelToken: options?.cancelToken,
    }).then((data) => data.body);
  };

  export const put = async <T>(url: string, options?: IOption): Promise<T> => {
    return await request<T>({
      method: "PUT",
      url,
      headers: genHeader(options?.hasAuth, options?.headers),
      data: options?.body,
      cancelToken: options?.cancelToken,
    }).then((data) => data.body);
  };

  export const del = async <T>(url: string, options?: IOption): Promise<T> => {
    return await request<T>({
      method: "DELETE",
      url,
      headers: genHeader(options?.hasAuth, options?.headers),
      data: options?.body,
      cancelToken: options?.cancelToken,
    }).then((data) => data.body);
  };

  export const getJson = async <T>(url: string): Promise<T> => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching JSON:", error);
      throw error;
    }
  };

  export const cancelToken = axios.CancelToken.source();
}

export namespace websocket {
  export const connect = (url: string): WebSocket => {
    return new WebSocket(webSocketURL + url);
  };
}

export default http;
